<template>
  <v-container fluid class="onboarding-checklist">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="checklist-hero" elevation="0">
          <v-card-title class="checklist-hero__body">
            <v-icon color="primary" class="mr-3">mdi-clipboard-check-outline</v-icon>
            <div>
              <span class="checklist-hero__title">Finalize seu cadastro</span>
              <p class="checklist-hero__subtitle mb-0">
                Falta apenas um passo para destravar recebimentos imediatos e destacar seu negócio para mais clientes.
              </p>
            </div>
          </v-card-title>
        </v-card>

        <page-loading
          v-if="loading"
          message="Finalizando seu cadastro"
          subtitle="Verificando pendências do onboarding"
          :preview-count="3"
        />

        <template v-else>


          <div v-if="hasPendingSections">
            <v-row class="mt-2">
              <v-col
                cols="12"
                v-for="section in pendingSections"
                :key="section.id"
              >
                <v-card class="checklist-pending-card" elevation="0">
                  <v-card-title class="d-flex align-center">
                    <div>
                      <p class="mb-0 text-subtitle-1 font-weight-medium">{{ section.title }}</p>
                      <small class="text--secondary">{{ section.description }}</small>
                    </div>
                    <v-spacer />
                    <v-chip color="error" outlined small class="checklist-pending-chip">Pendente</v-chip>
                  </v-card-title>
                  <v-card-text>
                    <ul class="missing-list">
                      <li v-for="missing in section.missing" :key="missing">
                        {{ missing }}
                      </li>
                    </ul>

                    <div v-if="section.id === 'pixKey'" class="mt-4">
                      <v-text-field
                        v-model="pixKeyInput"
                        label="Chave Pix"
                        outlined
                        dense
                        :disabled="savingPix"
                        hide-details="auto"
                      />
                      <v-btn
                        color="primary"
                        class="mt-2"
                        :disabled="!pixKeyInput"
                        :loading="savingPix"
                        @click="savePixKey"
                      >
                        Salvar chave Pix
                      </v-btn>
                    </div>

                    <div v-else-if="section.id === 'bankAccount'" class="mt-4">
                      <v-chip
                        class="mb-3"
                        :color="pagbankConnect ? 'success' : 'error'"
                        text-color="white"
                        label
                      >
                        <v-icon left small>
                          {{ pagbankConnect ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ pagbankConnect ? 'Conectado' : 'Não conectado' }}
                      </v-chip>
                      <div class="d-flex flex-wrap">
                      <v-btn
                        color="#00A859"
                        class="mr-2 mb-2"
                        dark
                        :loading="connectingPagBank"
                        @click="handleConnectPagBank"
                      >
                        {{ pagbankConnect ? 'Reconectar PagBank' : 'Conectar com PagBank' }}
                      </v-btn>
                      </div>
                    </div>

                    <div v-else-if="section.id === 'businessHours' && partner" class="mt-4">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-select
                            :items="dayOptions"
                            v-model="businessHourForm.day"
                            label="Dia da semana"
                            outlined
                            dense
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="businessHourForm.startTime"
                            type="time"
                            label="Horário de abertura"
                            outlined
                            dense
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="businessHourForm.endTime"
                            type="time"
                            label="Horário de fechamento"
                            outlined
                            dense
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="2" class="d-flex align-end">
                          <v-btn
                            color="primary"
                            block
                            :disabled="!canAddBusinessHour"
                            @click="addBusinessHour"
                          >
                            Adicionar
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-simple-table v-if="partner.businessHours.length" class="mt-4">
                        <thead>
                          <tr>
                            <th>Dia</th>
                            <th>Início</th>
                            <th>Fim</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(hour, index) in partner.businessHours"
                            :key="`${hour.day}-${hour.startTime}-${index}`"
                          >
                            <td>{{ hour.day }}</td>
                            <td>{{ hour.startTime }}</td>
                            <td>{{ hour.endTime }}</td>
                            <td>
                              <v-btn icon small color="error" @click="removeBusinessHour(index)">
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>

                      <div class="d-flex justify-end mt-4">
                        <v-btn
                          color="primary"
                          :loading="savingBusinessHours"
                          :disabled="savingBusinessHours || !partner.businessHours.length"
                          @click="saveBusinessHours"
                        >
                          Salvar horários
                        </v-btn>
                      </div>
                    </div>

                    <div v-else-if="section.id === 'additionalInfo' && partner" class="mt-4">
                      <v-textarea
                        v-model="partner.description"
                        label="Descrição completa"
                        outlined
                        rows="3"
                        auto-grow
                        hide-details
                      />
                      <v-row class="mt-2">
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="partner.whatsapp"
                            label="WhatsApp"
                            outlined
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="partner.instagram"
                            label="Instagram"
                            outlined
                            hide-details
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-file-input
                            v-model="logoFile"
                            label="Logo do negócio"
                            prepend-icon="mdi-store"
                            outlined
                            dense
                            accept="image/*"
                            @change="handleImageSelection('LOGO', $event)"
                            @click:clear="removeImage('LOGO')"
                          />
                          <div v-if="logoPreview" class="image-preview">
                            <img :src="logoPreview" alt="Pré-visualização do logo" />
                          </div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-file-input
                            v-model="coverFile"
                            label="Imagem de banner"
                            prepend-icon="mdi-image"
                            outlined
                            dense
                            accept="image/*"
                            @change="handleImageSelection('COVER', $event)"
                            @click:clear="removeImage('COVER')"
                          />
                          <div v-if="coverPreview" class="image-preview">
                            <img :src="coverPreview" alt="Pré-visualização do banner" />
                          </div>
                        </v-col>
                      </v-row>
                      <div class="d-flex justify-end mt-4">
                        <v-btn
                          color="primary"
                          :loading="savingAdditionalInfo"
                          :disabled="savingAdditionalInfo"
                          @click="saveAdditionalInfo"
                        >
                          Salvar informações
                        </v-btn>
                      </div>
                    </div>

                    <div v-else class="mt-4">
                      <v-btn
                        text
                        color="primary"
                        @click="goToMyDataTab(section.id === 'businessHours' ? 'hours' : 'info')"
                      >
                        Atualizar informações
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <div class="mt-6 d-flex flex-column flex-md-row justify-space-between align-center">
              <v-btn text color="primary" class="mb-3 mb-md-0" @click="handleSkip">
                Configurar depois
              </v-btn>
              <v-btn color="primary" dark @click="goToMyDataTab('bank')">
                Ir para meus dados
              </v-btn>
            </div>
          </div>
        </template>
      </v-col>
    </v-row>

    <PagBankConnectDialog
      v-model="showPagBankConnectDialog"
      @connected="handlePagBankConnected"
    />
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { PartnerDetailsType } from "@/types/partner/Partner";
import { GetPartner } from "@/services/partners/GetPartner";
import { UpdatePartner } from "@/services/partners/UpdatePartner";
import PagBankConnectDialog from "@/components/pagbank/PagBankConnectDialog.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import { GetPagBankStatus } from "@/services/partners/GetPagBankStatus";
import {
  ChecklistSection,
  hasPendingOnboardingItems,
  markOnboardingSkipped,
  clearOnboardingSkip,
  validateOnboardingData,
} from "@/utils/onboardingChecklist";

type MyDataTab = "bank" | "hours" | "info";

export default defineComponent({
  components: {
    PagBankConnectDialog,
    PageLoading,
  },
  data() {
    return {
      loading: true,
      partner: null as PartnerDetailsType | null,
      sections: [] as ChecklistSection[],
      pixKeyInput: "",
      savingPix: false,
      connectingPagBank: false,
      showPagBankConnectDialog: false,
      pagbankConnect: false,
      businessHourForm: {
        day: "",
        startTime: "",
        endTime: "",
      },
      dayOptions: [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ],
      savingBusinessHours: false,
      savingAdditionalInfo: false,
      logoFile: null as File | null,
      coverFile: null as File | null,
      logoPreview: "",
      coverPreview: "",
    };
  },
  computed: {
    pendingSections(): ChecklistSection[] {
      return this.sections.filter((section) => section.missing.length > 0);
    },
    hasPendingSections(): boolean {
      return hasPendingOnboardingItems(this.sections);
    },
    canAddBusinessHour(): boolean {
      const { day, startTime, endTime } = this.businessHourForm;
      return Boolean(day && startTime && endTime);
    },
  },
  methods: {
    loadCachedSections() {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return;
      }
      const cachedSections = window.sessionStorage.getItem("pendingOnboardingSections");
      if (!cachedSections) {
        return;
      }
      try {
        const parsed = JSON.parse(cachedSections);
        if (Array.isArray(parsed)) {
          this.sections = parsed as ChecklistSection[];
        }
      } catch (error) {
        console.warn("Não foi possível interpretar os itens pendentes em cache.", error);
      }
    },
    syncChecklistCache(sections: ChecklistSection[]) {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return;
      }
      if (hasPendingOnboardingItems(sections)) {
        window.sessionStorage.setItem("pendingOnboardingSections", JSON.stringify(sections));
      } else {
        window.sessionStorage.removeItem("pendingOnboardingSections");
      }
    },
    async loadPartner(showSkeleton = true) {
      if (showSkeleton) {
        this.loading = true;
      }
      try {
        const getPartner = new GetPartner();
        const partnerData = await getPartner.execute();
        const normalized = this.normalizePartner(partnerData);
        await this.loadPagBankStatus(normalized);
        this.partner = normalized;
        this.pixKeyInput = normalized.pixkey || "";
        const { sections } = validateOnboardingData(normalized);
        this.sections = sections;
        this.syncChecklistCache(this.sections);
        this.$store.commit("SET_ONBOARDING_PENDING", hasPendingOnboardingItems(sections));
        this.logoPreview = this.getImagePreview("LOGO");
        this.coverPreview = this.getImagePreview("COVER");
        this.logoFile = null;
        this.coverFile = null;
        this.businessHourForm = {
          day: "",
          startTime: "",
          endTime: "",
        };

        if (!this.hasPendingSections) {
          clearOnboardingSkip(normalized.id);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do parceiro:", error);
        this.$store.dispatch("showSnack", {
          message: "Não foi possível carregar seus dados.",
          color: "error",
        });
      } finally {
        if (showSkeleton) {
          this.loading = false;
        }
      }
    },
    normalizePartner(partner: PartnerDetailsType): PartnerDetailsType {
      const defaultAddress = {
        state: "",
        street: "",
        addressNumber: "",
        neighborhood: "",
        complement: "",
        zipCode: "",
        cityName: "",
        ibge: "",
      };
      const defaultUser = {
        name: "",
        nickname: "",
        motherName: "",
        birthdate: "",
        email: "",
        phone: "",
        document: "",
        password: "",
      };

      const normalizedUser = partner.users?.[0] || partner.user || defaultUser;

      return {
        ...partner,
        address: { ...defaultAddress, ...(partner.address || {}) },
        user: { ...defaultUser, ...normalizedUser },
        users: partner.users?.length ? partner.users : normalizedUser ? [normalizedUser] : [],
        businessHours: partner.businessHours || [],
        images: (partner.images || []).map((image) => ({
          ...image,
          status: image.status || "unchanged",
        })),
        pixkey: partner.pixkey || "",
        description: partner.description || "",
        whatsapp: partner.whatsapp || "",
        instagram: partner.instagram || "",
      };
    },
    async savePixKey() {
      if (!this.partner) {
        return;
      }
      const pixKey = this.pixKeyInput.trim();
      if (!pixKey) {
        this.$store.dispatch("showSnack", {
          message: "Informe uma chave Pix válida.",
          color: "warning",
        });
        return;
      }

      this.savingPix = true;
      try {
        this.partner.pixkey = pixKey;
        const updatePartner = new UpdatePartner();
        await updatePartner.execute(this.partner);
        this.$store.dispatch("showSnack", {
          message: "Chave Pix salva com sucesso!",
          color: "success",
        });
        await this.loadPartner(false);
      } catch (error) {
        console.error("Erro ao salvar chave Pix:", error);
        this.$store.dispatch("showSnack", {
          message: "Erro ao salvar chave Pix.",
          color: "error",
        });
      } finally {
        this.savingPix = false;
      }
    },
    async loadPagBankStatus(partner: PartnerDetailsType) {
      try {
        const getPagBankStatus = new GetPagBankStatus();
        const status = await getPagBankStatus.execute();
        this.pagbankConnect = !!status.pagbankConnect;
        partner.pagbankConnect = this.pagbankConnect;
      } catch (error) {
        console.error("Erro ao verificar status PagBank:", error);
        this.pagbankConnect =
          partner.digitalAccountNumber?.startsWith("ACCO") || false;
        partner.pagbankConnect = this.pagbankConnect;
      }
    },
    handleConnectPagBank() {
      this.showPagBankConnectDialog = true;
    },
    async handlePagBankConnected() {
      await this.loadPartner();
    },
    addBusinessHour() {
      if (!this.partner) {
        return;
      }
      if (!this.canAddBusinessHour) {
        this.$store.dispatch("showSnack", {
          message: "Preencha dia e horários antes de adicionar.",
          color: "warning",
        });
        return;
      }
      const newHour = {
        day: this.businessHourForm.day,
        startTime: this.businessHourForm.startTime,
        endTime: this.businessHourForm.endTime,
      };
      this.partner.businessHours.push(newHour);
      this.businessHourForm = {
        day: "",
        startTime: "",
        endTime: "",
      };
    },
    removeBusinessHour(index: number) {
      if (!this.partner) {
        return;
      }
      this.partner.businessHours.splice(index, 1);
    },
    async saveBusinessHours() {
      if (!this.partner) {
        return;
      }
      if (!this.partner.businessHours.length) {
        this.$store.dispatch("showSnack", {
          message: "Cadastre pelo menos um horário antes de salvar.",
          color: "warning",
        });
        return;
      }
      this.savingBusinessHours = true;
      try {
        await this.savePartnerChanges("Horários atualizados com sucesso!");
      } finally {
        this.savingBusinessHours = false;
      }
    },
    async saveAdditionalInfo() {
      if (!this.partner) {
        return;
      }
      const hasDescription = Boolean(this.partner.description?.trim());
      const hasWhatsapp = Boolean(this.partner.whatsapp?.trim());
      const hasInstagram = Boolean(this.partner.instagram?.trim());
      const hasLogo = Boolean(
        this.partner.images?.some(
          (image) => image.type === "LOGO" && image.image?.length
        )
      );
      const hasBanner = Boolean(
        this.partner.images?.some(
          (image) => image.type === "COVER" && image.image?.length
        )
      );

      if (!hasDescription || !hasWhatsapp || !hasInstagram || !hasLogo || !hasBanner) {
        this.$store.dispatch("showSnack", {
          message: "Informe descrição, WhatsApp, Instagram, logo e banner.",
          color: "warning",
        });
        return;
      }

      this.savingAdditionalInfo = true;
      try {
        await this.savePartnerChanges("Informações adicionais atualizadas!");
      } finally {
        this.savingAdditionalInfo = false;
      }
    },
    async savePartnerChanges(successMessage: string) {
      if (!this.partner) {
        return;
      }
      try {
        const updatePartner = new UpdatePartner();
        await updatePartner.execute(this.partner);
        this.$store.dispatch("showSnack", {
          message: successMessage,
          color: "success",
        });
        await this.loadPartner(false);
      } catch (error) {
        console.error("Erro ao atualizar dados do parceiro:", error);
        this.$store.dispatch("showSnack", {
          message: "Não foi possível salvar suas alterações.",
          color: "error",
        });
      }
    },
    handleImageSelection(type: "LOGO" | "COVER", fileInput: File | File[] | null) {
      if (!this.partner) {
        return;
      }
      const file = Array.isArray(fileInput) ? fileInput[0] : fileInput;
      if (!file) {
        this.removeImage(type);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64WithPrefix = reader.result as string;
        const cleanBase64 = base64WithPrefix.includes("base64,")
          ? base64WithPrefix.split("base64,")[1]
          : base64WithPrefix;
        this.upsertImage(type, cleanBase64);
        if (type === "LOGO") {
          this.logoPreview = base64WithPrefix;
        } else {
          this.coverPreview = base64WithPrefix;
        }
      };
      reader.readAsDataURL(file);
    },
    upsertImage(type: "LOGO" | "COVER", base64: string) {
      if (!this.partner) {
        return;
      }
      const currentImages = this.partner.images ? [...this.partner.images] : [];
      const filtered = currentImages.filter((image) => image.type !== type);
      filtered.push({
        type,
        image: base64,
        status: "added",
      });
      this.partner.images = filtered;
    },
    removeImage(type: "LOGO" | "COVER") {
      if (!this.partner) {
        return;
      }
      if (!this.partner.images) {
        this.partner.images = [];
      }
      this.partner.images = this.partner.images.filter(
        (image) => image.type !== type
      );
      if (type === "LOGO") {
        this.logoFile = null;
        this.logoPreview = "";
      } else {
        this.coverFile = null;
        this.coverPreview = "";
      }
    },
    getImagePreview(type: "LOGO" | "COVER"): string {
      if (!this.partner || !this.partner.images) {
        return "";
      }
      const image = this.partner.images.find((item) => item.type === type);
      if (!image || !image.image) {
        return "";
      }
      if (image.image.startsWith("http") || image.image.startsWith("data:")) {
        return image.image;
      }
      return `data:image/jpeg;base64,${image.image}`;
    },
    goToMyDataTab(tab: MyDataTab) {
      this.$router.push({ path: "/my-data", query: { tab } });
    },
    handleSkip() {
      markOnboardingSkipped(this.partner?.id);
      this.$store.dispatch("showSnack", {
        message: "Você poderá configurar essas informações quando quiser.",
        color: "info",
      });
      this.goToDashboard();
    },
    goToDashboard() {
      this.$router.push("/");
    },
  },
  mounted() {
    this.loadCachedSections();
    this.loadPartner();
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.onboarding-checklist {
  min-height: 100vh;
  background-color: $br-bg;
  padding-top: 32px;
  padding-bottom: 32px;
}

.checklist-hero {
  border-radius: $br-radius-lg !important;
  border: 1px solid rgba(229, 24, 61, 0.15) !important;
  background: linear-gradient(135deg, rgba(229, 24, 61, 0.06), $br-bg-elevated) !important;
  box-shadow: $br-shadow-sm !important;
  margin-bottom: $br-space-4;
}

.checklist-hero__body {
  display: flex;
  align-items: flex-start;
  padding: 20px !important;
}

.checklist-hero__title {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: $br-ink-soft;
}

.checklist-hero__subtitle {
  margin-top: 6px;
  font-size: $br-font-size-sm;
  color: $br-muted;
  line-height: 1.45;
}

.checklist-pending-card {
  border-radius: $br-radius-md !important;
  border: 1px solid $br-border-soft !important;
  background: $br-bg-elevated !important;
  box-shadow: $br-shadow-sm !important;
}

.checklist-pending-chip {
  font-weight: 600;
}

.missing-list {
  padding-left: 18px;
  margin: 0;
  color: $br-muted;
}

.missing-list li {
  margin-bottom: 4px;
}

.image-preview {
  margin-top: 8px;
}

.image-preview img {
  max-width: 220px;
  border-radius: $br-radius-sm;
  border: 1px solid $br-border-soft;
}
</style>

